// [LEGACY] ConversationDetailFlowView is not currently in use.
//
// Originally this component visualised AI processing steps (fraktal / hegel / fast-thinking)
// fetched from the root process collections (fraktal_processes, hegel_processes,
// fast_thinking_processes) via GET /conversations/{id}/details.
//
// After the 2.0 refactor all conversations live in the unified graph_conversations
// collection and the old process collections are no longer populated. This view —
// along with the backend endpoint and the ProcessStepNode component — should be
// reworked to read from graph_conversations before being re-enabled.
//
// Entry point (currently disabled): the "View Flow" button in UserDetailView's
// conversation table, which navigates to
// /dashboard/users/:userId/conversations/:conversationId/:conversationType.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import { useAdminApi } from '../hooks/useAdminApi';
import { ConversationDetailResponse, ConversationType } from '../types/adminApiTypes';
import ProcessStepNode from '../components/ProcessStepNode';

// Define the custom node type
const nodeTypes = {
  processStep: ProcessStepNode,
};

// Define initial layout properties
const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

const ConversationDetailFlowView: React.FC = () => {
  const { userId, conversationId, conversationType } = useParams<{
    userId: string;
    conversationId: string;
    conversationType: ConversationType;
  }>();

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const {
    data: detailData,
    loading: detailLoading,
    error: detailError,
    fetchData: fetchDetailData,
  } = useAdminApi<ConversationDetailResponse>();

  useEffect(() => {
    if (userId && conversationId && conversationType) {
      console.log('[ConversationDetailFlowView] Fetching details for:', {
        userId,
        conversationId,
        conversationType,
      });
      fetchDetailData({
        endpoint: `/conversations/${conversationId}/details`,
        user_id: userId,
        conversation_type: conversationType,
      });
    } else {
      console.error('[ConversationDetailFlowView] Missing parameters in URL:', {
        userId,
        conversationId,
        conversationType,
      });
    }
  }, [userId, conversationId, conversationType, fetchDetailData]);

  // Effect to transform API data into nodes and edges
  useEffect(() => {
    if (detailData?.process_details) {
      console.log('[ConversationDetailFlowView] Transforming process details into nodes/edges.');
      const newNodes: Node[] = [];
      const newEdges: Edge[] = [];
      let yPos = 50;
      const xPos = 100;
      const yIncrement = 400;

      detailData.process_details.forEach((processStep, index) => {
        const nodeId = `process-${index}`;
        const nodeLabel = `Step ${index + 1}: ${processStep?.type || 'Unknown Type'}`;

        newNodes.push({
          id: nodeId,
          type: 'processStep',
          data: {
            label: nodeLabel,
            processData: processStep,
          },
          position: { x: xPos, y: yPos },
        });

        if (index > 0) {
          newEdges.push({
            id: `edge-${index - 1}-${index}`,
            source: `process-${index - 1}`,
            target: nodeId,
            type: 'smoothstep',
            sourceHandle: 'bottom',
            targetHandle: 'top',
            animated: true,
          });
        }
        yPos += yIncrement;
      });

      console.log('[ConversationDetailFlowView] Generated Nodes:', JSON.stringify(newNodes));
      console.log('[ConversationDetailFlowView] Generated Edges:', JSON.stringify(newEdges));
      setNodes(newNodes);
      setEdges(newEdges);
    } else {
      console.log(
        '[ConversationDetailFlowView] No process details found in data, clearing nodes/edges.'
      );
      setNodes([]);
      setEdges([]);
    }
  }, [detailData, setNodes, setEdges]);

  // Log when nodes/edges state actually changes
  useEffect(() => {
    console.log('[ConversationDetailFlowView] Nodes state updated:', nodes);
  }, [nodes]);
  useEffect(() => {
    console.log('[ConversationDetailFlowView] Edges state updated:', edges);
  }, [edges]);

  if (detailLoading) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
  }

  if (detailError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading conversation details: {detailError}
      </Alert>
    );
  }

  if (nodes.length === 0 && !detailLoading) {
    if (detailData && detailData.process_details?.length === 0) {
      return <Typography sx={{ m: 2 }}>No process steps found for this conversation.</Typography>;
    }
    return <Typography sx={{ m: 2 }}>No details available or still loading nodes.</Typography>;
  }

  return (
    <Paper
      sx={{
        height: 'calc(100vh - 150px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        m: 0,
      }}
    >
      <Typography variant="h6" sx={{ p: 2, flexShrink: 0 }}>
        Conversation Flow: {conversationId} ({conversationType})
      </Typography>
      <Box sx={{ flexGrow: 1, height: '100%', width: '100%' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="top-right"
        >
          <Controls />
          <Background />
        </ReactFlow>
      </Box>
    </Paper>
  );
};

export default ConversationDetailFlowView;
